/**
 * 3D Room Visualizer — Wood grain
 *
 * Procedural, seamless wood for the framing lumber and the wooden doors. No
 * image files: each texture is drawn once into a canvas from layered noise and
 * shared by every material that uses it.
 *
 * How the grain is made, per pixel (u across the board, v along it):
 *   - growth rings are a cosine across the board, so they read as lines running
 *     its length;
 *   - low-frequency noise bends them, so the lines wander and pinch the way
 *     flat-sawn boards do;
 *   - knots (pine only) push the rings around themselves and darken the middle;
 *   - a fine streaky noise, stretched hard along the board, gives the fibre.
 * Every noise is periodic and the ring count is whole, so the tile repeats with
 * no seam.
 *
 * The texture is nearly white: it multiplies the material's own colour, so one
 * grain serves walnut, cedar or anything else. The same image is used as the
 * bump map, so the grain catches the light a little.
 *
 * One tile covers WOOD_TILE feet of real wood; `setGrainUVs` lays UVs out at
 * that scale, with the grain running along a chosen axis.
 */

import * as THREE from 'three';

export type WoodKind = 'fine' | 'pine';

/** How much real wood one repeat of the texture covers, in feet. */
export const WOOD_TILE = { across: 0.75, along: 3 };

const WIDTH = 512;   // across the grain
const HEIGHT = 1024; // along it

/** What makes one wood look different from another. */
export interface WoodRecipe {
  /** Grain lines across one tile (a whole number, so the tile repeats cleanly). More = finer grain. */
  rings: number;
  /** How dark the grain lines are against the wood between them, 0..~0.4. */
  ringContrast: number;
  /** How much of each ring is dark line, 0 (hairlines) .. 1 (broad bands). */
  lineWidth: number;
  /** How far the lines wander and how unevenly they are spaced, 0 (ruled) .. ~0.6. */
  waviness: number;
  /** Fine streaks along the board, 0..~0.2. */
  fibre: number;
  /** Knots in one tile. */
  knots: number;
  /** How dark a knot's heart is, 0..~0.6. */
  knotDarkness: number;
}

/** Fixed per kind, so a wood keeps its pattern while its recipe is adjusted. */
const SEEDS: Record<WoodKind, number> = { fine: 7, pine: 23 };

export const DEFAULT_WOOD: Record<WoodKind, WoodRecipe> = {
  // Hardwood for doors: tight, fairly straight, no knots.
  fine: { rings: 12, ringContrast: 0.16, lineWidth: 0.45, waviness: 0.22, fibre: 0.09, knots: 0, knotDarkness: 0.22 },
  // Framing lumber: wide rings, a few pale knots.
  pine: { rings: 8, ringContrast: 0.13, lineWidth: 0.45, waviness: 0.16, fibre: 0.05, knots: 4, knotDarkness: 0.22 },
};

const recipes: Record<WoodKind, WoodRecipe> = { fine: { ...DEFAULT_WOOD.fine }, pine: { ...DEFAULT_WOOD.pine } };

/** Small deterministic generator, so the grain is the same on every load. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** Value noise that repeats every (px, py) cells; `fbm` stacks octaves of it. */
function makeNoise(random: () => number) {
  const table = new Float32Array(256);
  const perm = new Uint8Array(256);
  for (let i = 0; i < 256; i++) { table[i] = random(); perm[i] = i; }
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const t = perm[i]; perm[i] = perm[j]; perm[j] = t;
  }
  const lattice = (x: number, y: number) => table[perm[(perm[x & 255] + y) & 255]];

  const noise = (x: number, y: number, px: number, py: number): number => {
    const x0 = Math.floor(x), y0 = Math.floor(y);
    const fx = x - x0, fy = y - y0;
    const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
    const X0 = mod(x0, px), X1 = mod(x0 + 1, px), Y0 = mod(y0, py), Y1 = mod(y0 + 1, py);
    const top = lattice(X0, Y0) + (lattice(X1, Y0) - lattice(X0, Y0)) * sx;
    const bottom = lattice(X0, Y1) + (lattice(X1, Y1) - lattice(X0, Y1)) * sx;
    return top + (bottom - top) * sy;
  };

  const fbm = (x: number, y: number, px: number, py: number, octaves: number): number => {
    let sum = 0, amplitude = 0.5, total = 0, f = 1;
    for (let o = 0; o < octaves; o++) {
      sum += amplitude * noise(x * f, y * f, px * f, py * f);
      total += amplitude;
      amplitude *= 0.5;
      f *= 2;
    }
    return sum / total;
  };

  return { noise, fbm };
}

function drawWood(kind: WoodKind, canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): void {
  const recipe = recipes[kind];
  const random = mulberry32(SEEDS[kind]);
  const { noise, fbm } = makeNoise(random);
  // Always place the same ten, and use the first few: adding a knot leaves the others where they were.
  const knots = Array.from({ length: 10 }, () => ({
    u: random(), v: random(), size: 0.7 + random() * 0.7,
  })).slice(0, Math.max(0, Math.round(recipe.knots)));
  const rings = Math.max(1, Math.round(recipe.rings));
  // Thin lines come from a steep curve: only the very middle of each ring's dark half shows.
  const sharpness = 0.5 + 6 * Math.pow(1 - Math.min(1, Math.max(0, recipe.lineWidth)), 2);
  // Distance on a wrapped axis, so a knot near an edge carries over to the far side.
  const wrapped = (d: number) => { const a = Math.abs(d); return Math.min(a, 1 - a); };

  const image = ctx.createImageData(WIDTH, HEIGHT);
  const data = image.data;
  for (let y = 0; y < HEIGHT; y++) {
    const v = y / HEIGHT;
    for (let x = 0; x < WIDTH; x++) {
      const u = x / WIDTH;

      // Where this pixel falls among the rings.
      // (The second term stretches and squeezes the spacing between rings.)
      // Along the board the noise is slow (one or two swells in a tile) — grain
      // runs in long, lazy curves, not wiggles.
      let phase = (u + (noise(u * 2, v * 2, 2, 2) - 0.5) * recipe.waviness) * rings
        + (noise(u * 5, v * 3, 5, 3) - 0.5) * recipe.waviness * 8;
      let knotDark = 0;
      for (const k of knots) {
        // A knot is about an inch across: wider than it is long in texture space.
        const du = wrapped(u - k.u) / (0.11 * k.size);
        const dv = wrapped(v - k.v) / (0.036 * k.size);
        const d2 = du * du + dv * dv;
        phase += 2.2 * Math.exp(-d2 / 6);      // rings swing round it
        knotDark += Math.exp(-d2 * 1.4);       // and its heart is dark
      }

      const ring = 0.5 + 0.5 * Math.cos(2 * Math.PI * phase);
      // The dark, dense part of each ring — stronger in some stretches of the
      // board than others, or the grain reads as ruled lines.
      const strength = 0.35 + 1.3 * fbm(u * 4, v * 2, 4, 2, 2);
      const late = Math.pow(ring, sharpness) * strength;
      const fibre = noise(u * 160, v * 9, 160, 9) - 0.5;          // streaks, stretched along the board
      const blotch = fbm(u * 2, v * 1, 2, 1, 2) - 0.5;            // broad, slow variation

      const value = Math.max(0.3, Math.min(1,
        0.94 + blotch * 0.08 - late * recipe.ringContrast - fibre * recipe.fibre - Math.min(1, knotDark) * recipe.knotDarkness));

      // Darker wood is a little redder, as it is.
      const i = (y * WIDTH + x) * 4;
      data[i] = Math.round(255 * value);
      data[i + 1] = Math.round(255 * Math.pow(value, 1.12));
      data[i + 2] = Math.round(255 * Math.pow(value, 1.32));
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  canvas.dataset.wood = kind;
}

const cache = new Map<WoodKind, THREE.Texture | null>();
const pending = new Map<WoodKind, ReturnType<typeof setTimeout>>();

/**
 * Change a wood's recipe. Every material using that wood shares one texture, so
 * they all change together. Redrawing takes a moment, so it waits until the
 * values have stopped moving (a slider being dragged), then calls `onDrawn` —
 * for a view that only draws when asked to.
 */
export function configureWood(kind: WoodKind, recipe: Partial<WoodRecipe>, onDrawn?: () => void): void {
  const next = { ...recipes[kind], ...recipe };
  const changed = (Object.keys(next) as (keyof WoodRecipe)[]).some((k) => next[k] !== recipes[kind][k]);
  if (!changed) return;
  recipes[kind] = next;
  const texture = cache.get(kind);
  if (!texture) return; // not drawn yet: it will be drawn to the new recipe when first used
  const waiting = pending.get(kind);
  if (waiting) clearTimeout(waiting);
  pending.set(kind, setTimeout(() => {
    pending.delete(kind);
    const canvas = texture.image as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawWood(kind, canvas, ctx);
    texture.needsUpdate = true;
    onDrawn?.();
  }, 140));
}

/** The recipe a wood is drawn to at the moment. */
export function getWoodRecipe(kind: WoodKind): WoodRecipe {
  return recipes[kind];
}

/**
 * The shared grain texture for `kind` (drawn on first use), or null where there
 * is no canvas to draw on (unit tests) — callers simply go without a map.
 * Shared: never dispose it with a material.
 */
export function getWoodTexture(kind: WoodKind): THREE.Texture | null {
  if (cache.has(kind)) return cache.get(kind)!;
  let texture: THREE.Texture | null = null;
  if (typeof document !== 'undefined' && process.env.NODE_ENV !== 'test') {
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      drawWood(kind, canvas, ctx);
      texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 8;
      texture.name = `wood-${kind}`;
    }
  }
  cache.set(kind, texture);
  return texture;
}

/** Give `material` the grain (and a little relief from it). No-op where there is no canvas. */
export function applyWood(material: THREE.MeshStandardMaterial, kind: WoodKind, relief = 0.35): void {
  const texture = getWoodTexture(kind);
  if (!texture) return;
  material.map = texture;
  material.bumpMap = texture;
  material.bumpScale = relief;
}

/** A repeatable 0..1 number from two coordinates — to start each board somewhere different in the grain. */
export function grainOffset(a: number, b: number): number {
  const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Lay UVs out so the grain runs along `lengthAxis` at real scale. Works on any
 * geometry made of flat faces (boxes, bevelled boxes): each vertex is mapped by
 * where it is, on the two axes its face lies in. `seed` shifts the board to its
 * own place in the texture, so neighbours don't show the same knots.
 */
export function setGrainUVs(geometry: THREE.BufferGeometry, lengthAxis: 'x' | 'y' | 'z', seed = 0): void {
  const position = geometry.attributes.position as THREE.BufferAttribute;
  const normal = geometry.attributes.normal as THREE.BufferAttribute | undefined;
  const uv = new Float32Array(position.count * 2);
  const axes = ['x', 'y', 'z'] as const;
  const offU = grainOffset(seed, 1.7);
  const offV = grainOffset(seed, 9.3);
  const p = [0, 0, 0];
  for (let i = 0; i < position.count; i++) {
    p[0] = position.getX(i); p[1] = position.getY(i); p[2] = position.getZ(i);
    // Which way the face looks decides which two axes it lies in.
    let facing = 2;
    if (normal) {
      const n = [Math.abs(normal.getX(i)), Math.abs(normal.getY(i)), Math.abs(normal.getZ(i))];
      facing = n[0] > n[1] && n[0] > n[2] ? 0 : n[1] > n[2] ? 1 : 2;
    }
    const length = axes.indexOf(lengthAxis);
    let across: number;
    let along: number;
    if (facing === length) {
      // End grain: just the two axes of the cut.
      const [a, b] = [0, 1, 2].filter((k) => k !== length);
      across = p[a];
      along = p[b];
    } else {
      across = p[[0, 1, 2].find((k) => k !== length && k !== facing)!];
      along = p[length];
    }
    uv[i * 2] = across / WOOD_TILE.across + offU;
    uv[i * 2 + 1] = along / WOOD_TILE.along + offV;
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}
